import React, { FC, useCallback } from "react";
import { Map } from "immutable";
import { Hidden, Visible } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import { OutgoingComponentCommonProps } from "types/outgoing";
import ParcelState from "../../../../components/ParcelState/ParcelState";
import ServiceLogo from "../../../../components/ServiceLogo/ServiceLogo";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  panelSelectData,
  panelSelectOpened,
} from "../../../../reduxFolder/selectors/rightPanelSelectors";
import {
  ExcessMarker,
  OutgoingEndCol,
  OutgoingGrowCol,
  OutgoingGrowText,
  OutgoingMainCol,
  OutgoingMetaCol,
} from "../../../../styles/outgoingStyles";
import { OutgoingParcelRow } from "../../../../styles/parcelsStyles";
import formatter from "../../../../utils/formatter";
import {
  SKU_COL_WIDTH,
  STATE_COL_WIDTH,
  WEIGHT_BRUTTO_COL_WIDTH,
} from "../rowLayout";
import { OutgoingCol, WeightAndPrice } from "./InProgress.styles";

const InProgress: FC<OutgoingComponentCommonProps> = React.memo(
  ({
    openRightPanel,
    parcel,
    isPanelOpened,
    panelData,
    units,
    isDemo = false,
    layoutTier = "full",
  }) => {
    const { t } = useTranslation("common");
    const deliveryName = parcel.get("preferred_carrier");
    const isSelectedShipment =
      isPanelOpened &&
      panelData &&
      panelData.get("detailedItemID") === parcel.get("id");
    const hasExcessItems = parcel.get("excess_items").size > 0;
    const showWeightCol = layoutTier === "full";
    const clickParcel = useCallback(
      () =>
        openRightPanel(
          "IN_PROGRESS",
          Map({
            detailedItemID: parcel.get("id"),
            excessItems: parcel.get("excess_items"),
            returnPreviousPanel: "IN_PROGRESS",
            isDemo,
          }),
        ),
      [openRightPanel, parcel, isDemo],
    );

    const state = parcel.get("state");

    const weight = () => {
      let weight = formatter.roundTo2Decimal(parcel.get("total_weight"));
      const unit = t(`units.${units}.primaryShort`);

      if (state === "paid") {
        const deliveryCost = parcel.getIn(["estimate", "price"], 0);
        return (
          <WeightAndPrice>{`${weight} ${unit}/$ ${deliveryCost}`}</WeightAndPrice>
        );
      } else {
        return `${weight} ${unit} (${t("parcels.brutto")})`;
      }
    };

    return (
      <OutgoingParcelRow selected={isSelectedShipment} onClick={clickParcel}>
        <Visible xs>
          <OutgoingCol xs={3}>
            {parcel.get("sku")}
            <ParcelState state={state} showText={false} />
          </OutgoingCol>
          <OutgoingCol xs={5}>
            <ServiceLogo serviceKey={deliveryName} />
            {deliveryName}
          </OutgoingCol>
          <OutgoingCol xs={4}>{weight()}</OutgoingCol>
        </Visible>
        <Hidden xs>
          <OutgoingMainCol sm={12}>
            <OutgoingMetaCol $width={SKU_COL_WIDTH} $grow={0}>
              {parcel.get("sku")}
              {hasExcessItems ? <ExcessMarker /> : null}
            </OutgoingMetaCol>
            <OutgoingGrowCol>
              <ServiceLogo serviceKey={deliveryName} />
              <OutgoingGrowText>{deliveryName}</OutgoingGrowText>
            </OutgoingGrowCol>
            {showWeightCol && (
              <OutgoingMetaCol $width={WEIGHT_BRUTTO_COL_WIDTH}>
                {weight()}
              </OutgoingMetaCol>
            )}
            <OutgoingEndCol $width={STATE_COL_WIDTH}>
              <ParcelState state={state} />
            </OutgoingEndCol>
          </OutgoingMainCol>
        </Hidden>
      </OutgoingParcelRow>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    isPanelOpened: panelSelectOpened,
    panelData: panelSelectData,
  }),
  { openRightPanel },
);

export default withConnect(InProgress);
